/**
 * infiltration-panel.ts — v0M5.4 (2026-07-25).
 *
 * Read-only settlement panel opened when the player LMB-clicks a
 * settlement that contains one of their own spies. Shows:
 *
 *   - Header — settlement name + tier + population (baseline info).
 *   - Infiltration level (0-3) + progress bar to next level, driven by
 *     `spy.infiltrationDays` and the SpecialUnits xlsx row
 *     (`infiltration_days_per_level` × `max_infiltration_level`).
 *   - Level-gated content per ESPIONAGE.md §2.2:
 *       Level 1 Basic     → completed structures list
 *       Level 2 Moderate  → build queue
 *       Level 3 Full      → recruit queue
 *   - Counter-Espionage indicator when the spy is inside a friendly
 *     settlement (auto-set on entry per apply-commands.ts).
 *   - Exit button → dispatches `exit_settlement_spy`; the spy
 *     rematerialises at the settlement cell + all infiltration state
 *     clears (per ESPIONAGE.md §2.2 exit-clears-intel).
 *
 * If multiple own spies are inside the same settlement (v0M5.4 allows
 * it), the panel picks the highest infiltration level and shows that
 * spy's info + Exit button. Multiple spies each contribute their own
 * CE roll independently in espionage.ts; the UI just surfaces the
 * best-informed one.
 */

import type { Command } from '../../sim/commands';
import type {
  GameState,
  FactionId,
  Province,
  Settlement,
  SettlementId,
  Spy,
} from '../../sim/state';
import { sortedIds } from '../../sim/state';
import { loadSpecialUnits } from '../../data/special-units';

const IP_SPECIAL_UNITS = loadSpecialUnits();

export interface InfiltrationPanelCallbacks {
  onIssue(cmd: Command): void;
  onClose(): void;
}

export interface InfiltrationPanelHandle {
  sync(
    state: GameState,
    openSettlementId: SettlementId | null,
    playerFactionId: FactionId | null,
  ): void;
  destroy(): void;
}

export function mountInfiltrationPanel(
  root: HTMLElement,
  callbacks: InfiltrationPanelCallbacks,
): InfiltrationPanelHandle {
  const backdrop = document.createElement('div');
  backdrop.className = 'su-infiltration-backdrop';
  backdrop.style.cssText = [
    'position:fixed', 'top:0', 'left:0', 'right:0', 'bottom:0',
    'background:rgba(0,0,0,0.4)', 'display:none', 'z-index:2500',
    'align-items:center', 'justify-content:center',
  ].join(';');

  const panel = document.createElement('div');
  panel.className = 'su-infiltration-panel';
  panel.style.cssText = [
    'background:rgba(20, 16, 10, 0.98)', 'border:2px solid #8a6d3b',
    'border-radius:4px', 'padding:16px 20px', 'color:#f5eacf',
    'font-family:"Trajan Pro","Cinzel","Times New Roman",serif',
    'min-width:420px', 'max-width:560px', 'max-height:80vh',
    'overflow-y:auto', 'box-shadow:0 8px 24px rgba(0,0,0,0.7)',
  ].join(';');
  backdrop.appendChild(panel);
  root.appendChild(backdrop);

  // Structural children — created once, updated in refresh().
  const header = document.createElement('div');
  header.style.cssText = 'display:flex; justify-content:space-between; align-items:baseline; margin-bottom:10px;';
  const titleEl = document.createElement('div');
  titleEl.style.cssText = 'font-size:16px; font-weight:bold;';
  const closeBtn = document.createElement('button');
  closeBtn.textContent = '×';
  closeBtn.style.cssText = 'background:none; color:#f5eacf; border:none; font-size:20px; cursor:pointer;';
  closeBtn.addEventListener('click', () => callbacks.onClose());
  header.appendChild(titleEl);
  header.appendChild(closeBtn);
  panel.appendChild(header);

  const subEl = document.createElement('div');
  subEl.style.cssText = 'opacity:0.85; margin-bottom:12px; font-size:13px;';
  panel.appendChild(subEl);

  const infilLabel = document.createElement('div');
  infilLabel.style.cssText = 'font-size:13px; margin-bottom:4px;';
  panel.appendChild(infilLabel);

  const barOuter = document.createElement('div');
  barOuter.style.cssText = 'width:100%; height:14px; background:#3a2c18; border:1px solid #6a5238; border-radius:2px; margin-bottom:10px; overflow:hidden;';
  const barInner = document.createElement('div');
  barInner.style.cssText = 'height:100%; background:linear-gradient(90deg, #b48b3a, #f0c66a); width:0%; transition:width 0.3s;';
  barOuter.appendChild(barInner);
  panel.appendChild(barOuter);

  const ceLabel = document.createElement('div');
  ceLabel.style.cssText = 'font-size:13px; margin-bottom:10px; color:#f0c66a;';
  panel.appendChild(ceLabel);

  // Gated content sections — hidden below their level, visible at/above.
  const section1 = makeSection('Level 1 — Completed structures');
  const section2 = makeSection('Level 2 — Build queue');
  const section3 = makeSection('Level 3 — Recruit queue');
  panel.appendChild(section1.container);
  panel.appendChild(section2.container);
  panel.appendChild(section3.container);

  // v0M5.5 (2026-07-25) — Steal + Sabotage orders (enemy settlement only,
  // gated by knowledge unlocks Foreign Accounts + Insurgency Techniques
  // respectively). Both surface as a row of buttons under the level
  // sections; the sabotage row also contains a structure-picker select.
  const ordersSection = document.createElement('div');
  ordersSection.style.cssText = 'margin-top:14px; padding-top:10px; border-top:1px solid #6a5238; display:none;';
  const ordersHeader = document.createElement('div');
  ordersHeader.textContent = 'Espionage Orders';
  ordersHeader.style.cssText = 'font-weight:bold; margin-bottom:8px; font-size:13px; opacity:0.95;';
  ordersSection.appendChild(ordersHeader);

  // Steal row
  const stealRow = document.createElement('div');
  stealRow.style.cssText = 'display:flex; align-items:center; gap:8px; margin-bottom:8px;';
  const stealBtn = document.createElement('button');
  stealBtn.textContent = 'Start Steal';
  stealBtn.style.cssText = [
    'padding:6px 12px', 'background:#5a4a2a', 'color:#f5eacf',
    'border:1px solid #b48b3a', 'border-radius:3px', 'cursor:pointer',
    'font-family:inherit', 'font-size:13px', 'flex:0 0 auto',
  ].join(';');
  const stealStatus = document.createElement('div');
  stealStatus.style.cssText = 'font-size:12px; opacity:0.9;';
  stealRow.appendChild(stealBtn);
  stealRow.appendChild(stealStatus);
  ordersSection.appendChild(stealRow);

  // Sabotage row
  const sabotageRow = document.createElement('div');
  sabotageRow.style.cssText = 'display:flex; align-items:center; gap:8px; margin-bottom:4px; flex-wrap:wrap;';
  const sabotageSelect = document.createElement('select');
  sabotageSelect.style.cssText = [
    'padding:5px', 'background:#3a2c18', 'color:#f5eacf',
    'border:1px solid #6a5238', 'font-family:inherit', 'font-size:12px',
    'min-width:160px',
  ].join(';');
  const sabotageBtn = document.createElement('button');
  sabotageBtn.textContent = 'Start Sabotage';
  sabotageBtn.style.cssText = [
    'padding:6px 12px', 'background:#7a3a2a', 'color:#f5eacf',
    'border:1px solid #b48b3a', 'border-radius:3px', 'cursor:pointer',
    'font-family:inherit', 'font-size:13px', 'flex:0 0 auto',
  ].join(';');
  const sabotageCancelBtn = document.createElement('button');
  sabotageCancelBtn.textContent = 'Cancel';
  sabotageCancelBtn.style.cssText = [
    'padding:6px 10px', 'background:#4a3020', 'color:#f5eacf',
    'border:1px solid #6a5238', 'border-radius:3px', 'cursor:pointer',
    'font-family:inherit', 'font-size:12px', 'flex:0 0 auto', 'display:none',
  ].join(';');
  sabotageRow.appendChild(sabotageSelect);
  sabotageRow.appendChild(sabotageBtn);
  sabotageRow.appendChild(sabotageCancelBtn);
  ordersSection.appendChild(sabotageRow);
  const sabotageStatus = document.createElement('div');
  sabotageStatus.style.cssText = 'font-size:12px; opacity:0.9; margin-top:4px;';
  ordersSection.appendChild(sabotageStatus);

  panel.appendChild(ordersSection);

  // Exit button
  const exitBtn = document.createElement('button');
  exitBtn.textContent = 'Exit Settlement';
  exitBtn.style.cssText = [
    'margin-top:12px', 'width:100%', 'padding:8px 14px',
    'background:#8a5a3b', 'color:#f5eacf', 'border:1px solid #b48b3a',
    'border-radius:3px', 'font-family:inherit', 'font-size:14px',
    'cursor:pointer',
  ].join(';');
  panel.appendChild(exitBtn);

  let currentSpyId: number | null = null;
  let currentPlayerFactionId: FactionId | null = null;

  exitBtn.addEventListener('click', () => {
    if (currentSpyId === null || currentPlayerFactionId === null) return;
    callbacks.onIssue({
      type: 'exit_settlement_spy',
      tick: 0,
      playerFactionId: currentPlayerFactionId,
      spyId: currentSpyId as unknown as import('../../sim/state').SpyId,
    });
    callbacks.onClose();
  });

  // v0M5.5 Steal toggle
  let currentStealActive = false;
  stealBtn.addEventListener('click', () => {
    if (currentSpyId === null || currentPlayerFactionId === null) return;
    callbacks.onIssue({
      type: 'set_spy_steal',
      tick: 0,
      playerFactionId: currentPlayerFactionId,
      spyId: currentSpyId as unknown as import('../../sim/state').SpyId,
      value: !currentStealActive,
    });
  });

  // v0M5.5 Sabotage — start with selected structureIndex
  sabotageBtn.addEventListener('click', () => {
    if (currentSpyId === null || currentPlayerFactionId === null) return;
    const idx = Number.parseInt(sabotageSelect.value, 10);
    if (Number.isNaN(idx) || idx < 0) return;
    callbacks.onIssue({
      type: 'sabotage_structure_spy',
      tick: 0,
      playerFactionId: currentPlayerFactionId,
      spyId: currentSpyId as unknown as import('../../sim/state').SpyId,
      structureIndex: idx,
    });
  });
  // v0M5.5 Sabotage cancel
  sabotageCancelBtn.addEventListener('click', () => {
    if (currentSpyId === null || currentPlayerFactionId === null) return;
    callbacks.onIssue({
      type: 'cancel_spy_sabotage',
      tick: 0,
      playerFactionId: currentPlayerFactionId,
      spyId: currentSpyId as unknown as import('../../sim/state').SpyId,
    });
  });

  // Backdrop click closes.
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) callbacks.onClose();
  });

  function sync(
    state: GameState,
    openSettlementId: SettlementId | null,
    playerFactionId: FactionId | null,
  ): void {
    if (openSettlementId === null || playerFactionId === null) {
      backdrop.style.display = 'none';
      currentSpyId = null;
      return;
    }
    const settlement = state.settlements.get(openSettlementId);
    if (!settlement) {
      backdrop.style.display = 'none';
      return;
    }
    // Find the highest-level own spy inside this settlement.
    const bestSpy = findBestSpyInSettlement(state, openSettlementId, playerFactionId);
    if (!bestSpy) {
      // No own spy inside → close.
      backdrop.style.display = 'none';
      currentSpyId = null;
      return;
    }
    const spec = IP_SPECIAL_UNITS[bestSpy.specialUnitType];
    if (!spec) {
      backdrop.style.display = 'none';
      return;
    }
    currentSpyId = bestSpy.id as unknown as number;
    currentPlayerFactionId = playerFactionId;
    backdrop.style.display = 'flex';

    const daysPerLevel = spec.infiltrationDaysPerLevel > 0 ? spec.infiltrationDaysPerLevel : 10;
    const maxLevel = spec.maxInfiltrationLevel > 0 ? spec.maxInfiltrationLevel : 3;
    const days = bestSpy.infiltrationDays ?? 0;
    const level = Math.min(maxLevel, Math.floor(days / daysPerLevel));
    const daysIntoLevel = days - level * daysPerLevel;
    const daysToNext = daysPerLevel - daysIntoLevel;
    const barPct = level >= maxLevel ? 100 : Math.min(100, (daysIntoLevel / daysPerLevel) * 100);

    const province = state.provinces.get(settlement.provinceId);
    titleEl.textContent = `Settlement — ${settlement.id} (Province ${settlement.provinceId})`;
    const ownerLabel = province?.ownerFactionId != null ? `owned by faction ${province.ownerFactionId}` : 'neutral';
    subEl.textContent = `${province?.tier ?? 'Unknown'} · ${ownerLabel} · pop ${Math.round(province?.population ?? 0).toLocaleString()}`;

    const levelName = ['None', 'Basic', 'Moderate', 'Full'][level] ?? `L${level}`;
    infilLabel.textContent =
      level >= maxLevel
        ? `Infiltration: Level ${level} ${levelName} (max)`
        : `Infiltration: Level ${level} ${levelName} — ${daysToNext.toFixed(1)}d to Level ${level + 1}`;
    barInner.style.width = `${barPct}%`;

    if (bestSpy.counterEspionage) {
      ceLabel.textContent = '⚑ Counter-Espionage active';
    } else {
      ceLabel.textContent = '';
    }

    // v0M5.5 (2026-07-25) — Steal + Sabotage orders section. Only
    // surfaces when the settlement is enemy-owned (province.ownerFactionId
    // differs from player's faction — includes neutral). Individual
    // buttons gated on the corresponding knowledge unlock.
    const isEnemyOwned = province != null && province.ownerFactionId !== playerFactionId;
    const faction = state.factions.get(playerFactionId);
    const researched = faction?.researchedKnowledge ?? [];
    const canSteal = researched.includes('Foreign Accounts');
    const canSabotage = researched.includes('Insurgency Techniques');
    const showOrders = isEnemyOwned && (canSteal || canSabotage);
    ordersSection.style.display = showOrders ? 'block' : 'none';

    if (showOrders && province) {
      // Steal row
      currentStealActive = !!bestSpy.stealActive;
      const daysActive = bestSpy.stealActiveDays ?? 0;
      // Read pct model — echoes espionage.ts (STEAL_BASE_PCT +
      // increment × floor(days / STEAL_INCREMENT_DAYS), cap 100%). At
      // UI it's imported as constants to avoid a sim-round-trip.
      const stealBasePct = 0.10;
      const stealIncrementPct = 0.05;
      const stealIncrementDays = 20;
      if (canSteal) {
        stealBtn.style.display = '';
        stealBtn.textContent = currentStealActive ? 'Stop Steal' : 'Start Steal';
        if (currentStealActive) {
          const tier = Math.floor(daysActive / stealIncrementDays);
          const pct = Math.min(1.0, stealBasePct + stealIncrementPct * tier);
          const daysToNext = stealIncrementDays - (daysActive % stealIncrementDays);
          const atCap = pct >= 1.0;
          stealStatus.textContent =
            `Stealing ${(pct * 100).toFixed(0)}% of settlement gold income`
            + (atCap ? ' (max)' : ` — +5% in ${daysToNext.toFixed(1)}d`);
        } else {
          stealStatus.textContent = 'Redirects growing % of enemy settlement gold income to you.';
        }
      } else {
        stealBtn.style.display = 'none';
        stealStatus.textContent = '(Steal requires Foreign Accounts research.)';
      }

      // Sabotage row
      const inFlight = bestSpy.sabotageTargetStructureIndex != null;
      if (canSabotage) {
        // Populate structure select from province.structures — but only
        // rebuild when the set actually changes.
        const listHash = province.structures.map((s) => s.name).join('|');
        if (sabotageSelect.dataset.listHash !== listHash) {
          sabotageSelect.innerHTML = '';
          const noneOpt = document.createElement('option');
          noneOpt.value = '-1';
          noneOpt.textContent = province.structures.length > 0
            ? 'Choose structure…'
            : '(no completed structures)';
          sabotageSelect.appendChild(noneOpt);
          province.structures.forEach((built, idx) => {
            const opt = document.createElement('option');
            opt.value = String(idx);
            opt.textContent = built.name;
            sabotageSelect.appendChild(opt);
          });
          sabotageSelect.dataset.listHash = listHash;
        }
        sabotageSelect.disabled = inFlight || province.structures.length === 0;
        sabotageBtn.disabled = inFlight || province.structures.length === 0
          || Number.parseInt(sabotageSelect.value, 10) < 0;
        sabotageBtn.style.display = inFlight ? 'none' : '';
        sabotageCancelBtn.style.display = inFlight ? '' : 'none';
        if (inFlight && bestSpy.sabotageTargetStructureIndex != null) {
          const idx = bestSpy.sabotageTargetStructureIndex;
          const target = province.structures[idx];
          const remaining = bestSpy.sabotageDaysRemaining ?? 0;
          sabotageStatus.textContent =
            `Sabotaging ${target?.name ?? 'structure'} — ${remaining.toFixed(0)}d remaining`;
        } else {
          sabotageStatus.textContent = 'Destroys a completed structure after 20 days. Owner is alerted on completion.';
        }
      } else {
        sabotageSelect.style.display = 'none';
        sabotageBtn.style.display = 'none';
        sabotageCancelBtn.style.display = 'none';
        sabotageStatus.textContent = '(Sabotage requires Insurgency Techniques research.)';
      }
    }

    // Gated content — populate what's unlocked, hide the rest.
    if (level >= 1 && province) {
      section1.container.style.display = 'block';
      renderStructureList(section1.list, province);
    } else {
      section1.container.style.display = 'none';
    }
    if (level >= 2 && province) {
      section2.container.style.display = 'block';
      renderBuildQueue(section2.list, province);
    } else {
      section2.container.style.display = 'none';
    }
    if (level >= 3 && province) {
      section3.container.style.display = 'block';
      renderRecruitQueue(section3.list, province);
    } else {
      section3.container.style.display = 'none';
    }
  }

  return {
    sync,
    destroy() {
      backdrop.remove();
    },
  };
}

/** Find the own spy with the highest infiltrationDays inside `settlementId`. */
function findBestSpyInSettlement(
  state: GameState,
  settlementId: SettlementId,
  playerFactionId: FactionId,
): Spy | null {
  if (!state.spies) return null;
  let best: Spy | null = null;
  let bestDays = -1;
  for (const id of sortedIds(state.spies)) {
    const spy = state.spies.get(id);
    if (!spy) continue;
    if (spy.ownerFactionId !== playerFactionId) continue;
    if (spy.insideSettlementId !== settlementId) continue;
    const days = spy.infiltrationDays ?? 0;
    if (days > bestDays) {
      bestDays = days;
      best = spy;
    }
  }
  return best;
}

function makeSection(title: string): { container: HTMLElement; list: HTMLElement } {
  const container = document.createElement('div');
  container.style.cssText = 'margin-top:10px; border-top:1px solid #4a3820; padding-top:8px;';
  const label = document.createElement('div');
  label.style.cssText = 'font-size:12px; opacity:0.85; margin-bottom:6px;';
  label.textContent = title;
  const list = document.createElement('div');
  list.style.cssText = 'font-size:13px; line-height:1.6;';
  container.appendChild(label);
  container.appendChild(list);
  return { container, list };
}

function renderStructureList(el: HTMLElement, province: Province): void {
  if (province.structures.length === 0) {
    el.textContent = 'No structures built.';
    return;
  }
  const counts = new Map<string, number>();
  for (const s of province.structures) counts.set(s.name, (counts.get(s.name) ?? 0) + 1);
  el.innerHTML = Array.from(counts.entries())
    .map(([name, n]) => `${n}× ${escapeHtml(name)}`)
    .join('<br>');
}

function renderBuildQueue(el: HTMLElement, province: Province): void {
  if (province.buildQueue.length === 0) {
    el.textContent = 'Nothing being built.';
    return;
  }
  el.innerHTML = province.buildQueue
    .map((b) => `${escapeHtml(b.name)} — ${b.progressSec.toFixed(1)}s in`)
    .join('<br>');
}

function renderRecruitQueue(el: HTMLElement, province: Province): void {
  if (province.recruitQueue.length === 0) {
    el.textContent = 'Nothing being recruited.';
    return;
  }
  el.innerHTML = province.recruitQueue
    .map((r) => `${escapeHtml(r.cohortType)} — ${r.progressSec.toFixed(1)}s in`)
    .join('<br>');
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

// Silence unused-var — Settlement used only via ownership check inside sync.
void ({} as Settlement);
